import * as React from 'react'
import { ReactElement, useEffect, useRef, useState } from 'react'
import { getTheme } from '@fluentui/react'
import { Input, InputProps, makeStyles } from '@fluentui/react-components'
import { CheckmarkCircleRegular, EditRegular, ErrorCircleRegular, SaveSyncRegular } from '@fluentui/react-icons'

const useStyles = makeStyles({
    inputGrow: {
        width: 'calc(100% - 5em)',
    },
})

export enum InputStatus {
    None = 'NONE',
    Success = 'SUCCESS',
    Failed = 'FAILED',
    Editing = 'EDITING',
    Saving = 'SAVING',
}

export type AutoSavingInputProps = InputProps & {
    status?: InputStatus
    statusIcon?: ReactElement
    save?: (value: string) => Promise<InputStatus | undefined>
    onFormat?: (value: string) => string
}

const statusIcons = new Map<InputStatus, { icon: ReactElement; color: string }>([
    [InputStatus.Success, { icon: <CheckmarkCircleRegular />, color: getTheme().palette.green }],
    [InputStatus.Failed, { icon: <ErrorCircleRegular />, color: getTheme().palette.red }],
    [InputStatus.Editing, { icon: <EditRegular />, color: getTheme().palette.neutralPrimary }],
    [InputStatus.Saving, { icon: <SaveSyncRegular />, color: getTheme().palette.neutralPrimary }],
])

export default function AutoSavingInput(props: AutoSavingInputProps): ReactElement {
    const ref = useRef<HTMLInputElement>()
    const styles = useStyles()

    const [value, setValue] = useState<string>(props.value || '')
    const [previousValue, setPreviousValue] = useState<string>(props.value || '')
    const [status, setStatus] = useState<InputStatus>(props.status || InputStatus.None)
    const [icon, setIcon] = useState<ReactElement>(props.statusIcon)

    useEffect(() => {
        if (props.status) {
            setStatus(props.status)
        }
    }, [props.value, props.status])

    useEffect(() => {
        setIcon(statusIcons.get(status)?.icon || props.statusIcon)
    }, [status])

    return (
        <Input
            className={value?.length > 15 && styles.inputGrow}
            {...props}
            onChange={(_, newValue) => setValue(props.onFormat?.(newValue?.value) ?? newValue?.value ?? '')}
            contentAfter={icon}
            value={value}
            ref={ref}
            onFocus={event => {
                if (props.save) {
                    setStatus(InputStatus.Editing)
                }
                props.onFocus?.(event)
            }}
            onBlur={event => {
                if (props.save) {
                    setStatus(InputStatus.Saving)
                    props.save(value).then(
                        (statusIcon: InputStatus) => {
                            setPreviousValue(value)
                            setStatus(statusIcon || InputStatus.Success)
                            setTimeout(() => setIcon(undefined), 3000)
                        },
                        () => setStatus(InputStatus.Failed),
                    )
                }
                props.onBlur?.(event)
            }}
            onKeyDown={event => {
                if (props.save) {
                    switch (event.key) {
                        case 'Escape':
                            setValue(previousValue)
                            ref.current?.blur()
                            break
                        case 'Enter':
                            ref.current?.blur()
                            break
                    }
                }
                props.onKeyDown?.(event)
            }}
        />
    )
}
